import Link from 'next/link'
import { ComponentProps, MouseEvent } from 'react'

import { Price } from 'components/common/Price'
import { Spinner } from 'components/common/Spinner'
import { useAuth, useAuthModal } from 'contexts'
import { useRouter } from 'next/router'
import styles from './TradeButton.module.css'

const themes = {
  green:
    'bg-emerald-800 border-emerald-800 hover:bg-emerald-900 hover:border-emerald-900',
  red: 'bg-chili-800 border-chili-800 hover:bg-chili-900 hover:border-chili-900',
  outline: 'bg-cool-50 border-cool-800 !text-cool-800 ',
  outlineGreen:
    'bg-cool-50 border-cool-800 !text-cool-800 hover:text-emerald-900',
  outlineRed: 'bg-cool-50 border-cool-800 !text-cool-800  hover:text-chili-800',
} as const

interface Props extends ComponentProps<typeof Link> {
  children?: React.ReactNode
  theme?: keyof typeof themes
  value?: number | null
  userCurrency?: 'USER' | 'GBP' | 'USD' | 'EUR'
  isLoading?: boolean
}

export const TradeButton = ({
  children,
  theme = 'outline',
  value,
  isLoading,
  userCurrency,
  ...props
}: Props) => {
  const { user } = useAuth()
  const { setConfig, setIsOpen, setState } = useAuthModal()
  const router = useRouter()

  const getOutlineHoverColor = (theme: string) => {
    if (theme === 'outlineGreen') {
      return 'group-hover:text-emerald-900'
    }
    if (theme === 'outlineRed') {
      return 'group-hover:text-chili-800'
    }
    return ''
  }

  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!user) {
      event.preventDefault()
      setConfig({
        onSuccess: () => {
          router.push(props.href)
        },
        socialLoginRedirectTo: props.href.toString(),
      })
      setState('register')
      setIsOpen(true)
    }
  }

  return (
    <Link
      {...props}
      className={`${
        value
          ? styles.tradeButton
          : `${styles.tradeButtonSimple} flex w-full flex-wrap justify-center px-3 py-3 text-center sm:px-6`
      }   group items-center rounded-sm border font-medium  text-cool-150 duration-200 ease-in-out lg:rounded ${
        themes[theme]
      } ${isLoading ? '!flex items-center justify-center' : ''}`}
      onClick={onClick}
    >
      {isLoading && (
        <Spinner theme={theme.includes('outline') ? 'purple' : 'white'} />
      )}

      {!isLoading && (
        <>
          <span className={`font-medium ${getOutlineHoverColor(theme)}`}>
            {children}
          </span>
          {value && (
            <>
              <span
                className={`${styles.tradeButtonSeparator} mx-[6px] h-4 w-[2px] rounded-[50px] bg-[#ffffff99] font-medium `}
              />
              <Price
                className='font-medium'
                formattingOptions={{
                  from: userCurrency,
                  to: userCurrency,
                }}
              >
                {value / 100}
              </Price>
            </>
          )}
        </>
      )}
    </Link>
  )
}
